import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { EnumService } from "./enum.service";
import { SharedDataService } from "./shared-data.service";
import { HTTP } from "@ionic-native/http/ngx";
import { File } from "@ionic-native/file/ngx";
import { Capacitor } from "@capacitor/core";
import { UtilService } from "./util.service";

import * as JSZip from "jszip";
import { FilehandlerService } from "./filehandler.service";
import { Zip } from "@ionic-native/zip/ngx";
import { Platform } from "@ionic/angular";
import { FileTransfer } from "@ionic-native/file-transfer/ngx";
import { of } from "rxjs";
import { tap, catchError } from "rxjs/operators";
import * as moment from "moment";

@Injectable({
  providedIn: "root",
})
export class OfflineApiService {
  constructor(
    private httpClient: HttpClient,
    private platform: Platform,
    private http: HTTP,
    private zip: Zip,
    private file: File,
    private fileTransfer: FileTransfer,
    public sharedDataService: SharedDataService,
    public filehandlerService: FilehandlerService
  ) {}

  getDeviceOfflineDetails(deviceId) {
    return this.httpClient.get(
      `${this.sharedDataService.apiBaseUrl}/${EnumService.ApiMethods.GetDeviceOfflineDetails}/${deviceId}`
    );
  }

  getSynchStatus(deviceId) {
    return this.httpClient.get(
      `${this.sharedDataService.apiBaseUrl}/${EnumService.ApiMethods.GetSynchStatus}/${deviceId}`
    );
  }

  deleteDownloadedFileFromServer(fileName) {
    return this.httpClient.delete(
      `${this.sharedDataService.apiBaseUrl}/${EnumService.ApiMethods.DeviceOfflineFileDelete}?fileName=${fileName}`
    );
  }

  downloadFile(path: string, headers) {
    return this.httpClient.get(`${path}`, { headers: headers });
  }

  getDeviceOfflineFile(files, progressCallBack = null) {
    return new Promise((resolve, reject) => {
      const unZippedFiles = [];

      const onFileDownloadError = (error) => {
        console.log("File Download Error ", error);

        if (typeof error === "object" && error?.message) {
          reject(error?.message);
        } else if (typeof error === "string") {
          reject(error);
        } else {
          reject("File download error");
        }
      };

      const accessID = this.sharedDataService.deviceUID;
      const token = localStorage.getItem(
        EnumService.LocalStorageKeys.API_TOKEN
      );
      let headers = {};
      if (accessID && token) {
        headers = { accessID, token };
      }

      const unZipFile = (localFilePath, fileIndex) => {
        this.zip
          .unzip(
            localFilePath,
            this.filehandlerService.offlineFilesDirectory(),
            (progress) => {
              if (progress.total > 0) {
                const percent = Math.round(
                  (progress.loaded / progress.total) * 100
                );

                const totalFiles = files.length * 2;
                const eachFilePercent = 100 / totalFiles;

                const finalPercent =
                  eachFilePercent * (fileIndex * 2 + 1) + percent / totalFiles;

                if (finalPercent > 0) {
                  progressCallBack && progressCallBack(finalPercent, "unzip");
                }

                console.log("Unzipping, " + finalPercent + "%");
              }
            }
          )
          .then((result) => {
            // UnZipping completed
            if (result === 0) {
              // For json file only
              const fileDetail = files[fileIndex];
              if (fileDetail.name !== "All_Files") {
                unZippedFiles.push(localFilePath.replace(".zip", ".json"));
              } else {
                const zipFileName = fileDetail.zipFileName;

                localStorage.setItem(
                  EnumService.LocalStorageKeys.OFFLINE_FILES_FOLDER_NAME,
                  zipFileName.replace(".zip", "")
                );
              }
              // Delete zip file
              this.filehandlerService
                .removeFile(localFilePath)
                .then(() => {
                  //Download Next zip file
                  if (++fileIndex < files.length) {
                    downloadFileFromServer(fileIndex);
                  } else {
                    // All files downloaded complete
                    resolve(unZippedFiles);
                  }
                })
                .catch((error) => {
                  reject(
                    new Error(
                      "Unable to remove downloaded zip file:: " + error.message
                    )
                  );
                });
            }
            // UnZipping Failed
            if (result === -1) {
              console.log("UnZipping FAILED");
              reject(new Error("Unable to unzip file " + fileIndex));
            }
          });
      };

      const downloadFileFromServer = (fileIndex) => {
        if (fileIndex < files.length) {
          const fileDetail = files[fileIndex];
          const jsonFileName = fileDetail.jsonFileName;
          const zipFileName = fileDetail.zipFileName;

          const fileName = UtilService.isLocalHost()
            ? jsonFileName
            : zipFileName;

          if (fileName) {
            let serverFileUrl = `${this.sharedDataService.apiBaseUrl}/${EnumService.ApiMethods.GetDeviceOfflineFile}?fileName=${fileName}`;

            const deviceFilePath =
              this.filehandlerService.offlineFilesDirectory() + fileName;

            console.log("Device_file_path ", deviceFilePath);
            if (UtilService.isLocalHost()) {
              resolve([serverFileUrl]);
            } else {
              const onFileDownloadCompleted = (response) => {
                console.log("File Download Completed ", response);

                // Remove files from server
                this.deleteDownloadedFileFromServer(fileName).subscribe(
                  (res) => {}
                );
                this.deleteDownloadedFileFromServer(zipFileName).subscribe(
                  (res) => {}
                );

                unZipFile(deviceFilePath, fileIndex);
              };

              let lastLoggedPercent = -1;
              const calculateProgress = (progress) => {
                if (progress.total > 0) {
                  const percent = Math.round(
                    (progress.loaded / progress.total) * 100
                  );

                  const totalFiles = files.length * 2;
                  const eachFilePercent = 100 / totalFiles;

                  const finalPercent =
                    eachFilePercent * fileIndex * 2 + percent / totalFiles;
                  if (finalPercent > 0) {
                    progressCallBack && progressCallBack(finalPercent);
                  }
                  // Only log when percentage changes by at least 0.1% to avoid duplicate logs
                  const roundedPercent = Math.round(finalPercent * 10) / 10;
                  if (roundedPercent !== lastLoggedPercent) {
                    console.log("Downloading  " + roundedPercent + "%");
                    lastLoggedPercent = roundedPercent;
                  }
                }
              };

              if (this.platform.is("android")) {
                // Loading with timer becuase
                let totalLoad = 100;
                let loaded = 1;
                const intervalRef = setInterval(() => {
                  loaded++;
                  if (loaded >= 100) {
                    clearInterval(intervalRef);
                    loaded = 100;
                  }
                  calculateProgress({ total: totalLoad, loaded });
                }, 1000);

                this.http
                  .downloadFile(serverFileUrl, {}, headers, deviceFilePath)
                  .then(async (response) => {
                    calculateProgress({ total: totalLoad, loaded: 100 });
                    clearInterval(intervalRef);

                    onFileDownloadCompleted(response);
                  })
                  .catch((error) => {
                    calculateProgress({ total: totalLoad, loaded: 100 });
                    clearInterval(intervalRef);
                    onFileDownloadError(error);
                  });
              } else {
                const fileTransfer = this.fileTransfer.create();
                fileTransfer
                  .download(serverFileUrl, deviceFilePath, false, {
                    headers: headers,
                  })
                  .then((response) => {
                    onFileDownloadCompleted(response);
                  })
                  .catch((error) => {
                    onFileDownloadError(error);
                  });

                fileTransfer.onProgress((progress) => {
                  calculateProgress(progress);
                });
              }
            }
          } else {
            onFileDownloadError(new Error("File not found on server"));
          }
        }
      };

      if (files && files.length > 0) {
        downloadFileFromServer(0);
      } else {
        onFileDownloadError(new Error("No files for download"));
      }
    });
  }

  getDeviceOfflineFileN(files) {
    return new Promise((resolve, reject) => {
      const downloadedJsonFileData = [];

      const onFileDownloaded = (jsonObject) => {
        downloadedJsonFileData.push(jsonObject);
        if (downloadedJsonFileData.length === files.length) {
          resolve(downloadedJsonFileData);
        }
      };

      // const isDemoFile = UtilService.isLocalHost();
      const isDemoFile = false;

      if (isDemoFile) {
        this.filehandlerService
          .readJsonFile("./assets/202108101317573870376.json")
          .then((response) => {
            resolve(response);
          });
      } else {
        const accessID = this.sharedDataService.deviceUID;
        const token = localStorage.getItem(
          EnumService.LocalStorageKeys.API_TOKEN
        );
        let headers = {};
        if (accessID && token) {
          headers = { accessID, token };
        }

        const readBlob = (blob) => {
          try {
            var jsZipObj = new JSZip();

            jsZipObj
              .loadAsync(blob)
              .then(function (zip) {
                // you now have every files contained in the loaded zip

                if (zip.length > 0) {
                  Object.keys(zip.files).map((zippedFileName) => {
                    zip
                      .file(zippedFileName)
                      .async("string")
                      .then((response) => {
                        onFileDownloaded(response);
                      })
                      .catch((error) => {}); // a promise of "Hello World\n"
                  });
                }
              })
              .catch((error) => {});
          } catch (error) {}
        };

        const sampleZipFile =
          "https://www.learningcontainer.com/wp-content/uploads/2020/05/sample-zip-file.zip";
        fetch(sampleZipFile)
          .then((res) => res.blob())
          .then((res) => {
            readBlob(res);
          })
          .catch((error) => {});

        return;

        files.map((obj: any) => {
          const fileName = obj.jsonFileName;
          let fileUrl = `${this.sharedDataService.apiBaseUrl}/${EnumService.ApiMethods.GetDeviceOfflineFile}?fileName=${fileName}`;
          const writeDirectory =
            this.filehandlerService.offlineFilesDirectory();
          const localFilePath = writeDirectory + fileName;

          const fileTransfer = this.fileTransfer.create();
          fileTransfer
            .download(fileUrl, localFilePath, true, {
              headers: headers,
              ...headers,
            })
            .then(
              (entry) => {
                //here logging our success downloaded file path in mobile.
                console.log("download completed: " + entry.toURL());
                readBlob(entry.toURL());
              },
              (error) => {
                //here logging our error its easier to find out what type of error occured.
                console.log("download failed: " + error);
              }
            );

          // this.http
          // 	.downloadFile(fileUrl, {}, headers, localFilePath)
          // 	.then(async (response) => {
          // 		console.log('File Download Completed ', response);
          //
          //
          // 		let { name, nativeURL } = response;
          // 		let path = nativeURL.substring(0, nativeURL.lastIndexOf('/'));
          // 		const fileName = name;
          // 		// if you already know the file extension, just assign it to           // variable below
          // 		const fileExtension = fileName.match(/\.[A-z0-9]+$/i)[0].slice(1);
          // 		// we are provided the name, so now read the file into a buffer
          // 		const buffer = await this.file.readAsArrayBuffer(path, name);
          //
          // 		if (buffer) {
          // 			let medBlob = new Blob([buffer], {
          // 				type: `application/${fileExtension}`,
          // 			});
          // 			readBlob(medBlob);
          // 		}
          // 	})
          // 	.catch((error) => {
          //
          // 		console.log('File Download Error ', error);
          // 	});
        });
      }
    });
  }

  synchDevice = (deviceId) => {
    return this.httpClient.post(
      `${this.sharedDataService.apiBaseUrl}/${EnumService.ApiMethods.SynchDevice}?id=${deviceId}`,
      {}
    );
  };

  uploadMultipleFiles = (formData) => {
    return this.httpClient.post(
      `${this.sharedDataService.apiBaseUrl}/${EnumService.ApiMethods.PostOfflineFormAnswerPhotoOrVideo}`,
      formData
    );
  };

  postOfflineZipFile = (fileObj, fileName) => {
    const formData = new FormData();
    formData.append("file", fileObj, fileName);
    return this.httpClient.post(
      `${this.sharedDataService.apiBaseUrl}/${EnumService.ApiMethods.PostDeviceOfflineFile}`,
      formData
    );
  };

  // New endpoints for incremental sync
  getPendingChanges(deviceId: string, lastSyncDate: string) {
    // Format lastSyncDate to ISO datetime format (YYYY-MM-DDTHH:mm:ss or YYYY-MM-DDTHH:mm:ss.sssZ)
    let formattedDate = lastSyncDate;
    if (lastSyncDate) {
      // If it's already an ISO string, use it as is
      if (lastSyncDate.includes('T')) {
        formattedDate = lastSyncDate;
      } else {
        // If it's just a date (YYYY-MM-DD), convert to datetime format
        // Use the date with time set to 00:00:00
        formattedDate = moment(lastSyncDate).format('YYYY-MM-DDTHH:mm:ss');
      }
    }
    
    // Backend expects deviceUniqueId (not deviceId) and lastSyncDate in datetime format
    const queryParams = `deviceUniqueId=${deviceId}&lastSyncDate=${encodeURIComponent(formattedDate)}`;
    return this.httpClient.get(
      `${this.sharedDataService.siteBaseUrl}/DeviceSync/GetPendingChanges?${queryParams}`,
      {
        headers: this.getHeaders(),
      }
    );
  }

  /**
   * Get sync data - can be used for both initial sync and incremental sync
   * @param request - Either SyncDataRequest for initial sync OR GetSyncDataRequest with changes for incremental sync
   */
  getSyncData(request: { 
    deviceId?: string; 
    companyId?: number; 
    includeLocations?: boolean; 
    includeForms?: boolean; 
    includeDocuments?: boolean; 
    changeIds?: string[];
    changes?: Array<{
      syncLogID: number;
      changeType: string;
      entityType: string;
      entityID: number;
      createdDate: string;
    }>;
  }) {
    const isInitialSync = !!request.deviceId && !!request.companyId;
    const isIncrementalSyncWithChanges = !!request.changes && request.changes.length > 0;
    const syncType = isInitialSync ? 'INITIAL SYNC' : (isIncrementalSyncWithChanges ? 'INCREMENTAL SYNC (with changes)' : 'INCREMENTAL SYNC (legacy)');
    
    console.log('========================================');
    console.log(`🔄 getSyncData() called - ${syncType}`);
    console.log('Request payload:', JSON.stringify(request, null, 2));
    console.log('========================================');

    const deviceId = request.deviceId || this.sharedDataService.deviceUID;
    if (!deviceId) {
      throw new Error('Device ID is required for sync');
    }

    // For incremental sync with changes, use POST /DeviceSync/GetSyncData
    if (isIncrementalSyncWithChanges) {
      const postRequest = {
        DeviceUniqueId: deviceId,
        Changes: request.changes.map((change: any) => ({
          SyncLogID: change.syncLogID || change.SyncLogID,
          ChangeType: change.changeType || change.ChangeType,
          EntityType: change.entityType || change.EntityType,
          EntityID: change.entityID || change.EntityID,
          CreatedDate: change.createdDate || change.CreatedDate
        }))
      };

      const url = `${this.sharedDataService.siteBaseUrl}/DeviceSync/GetSyncData`;
      console.log(`📡 [API] Calling POST: ${url}`);
      console.log(`📡 [API] Request body:`, JSON.stringify(postRequest, null, 2));

      return this.httpClient.post(
        url,
        postRequest,
        {
          headers: this.getHeaders(),
        }
      ).pipe(
        tap((response: any) => {
          console.log('========================================');
          console.log(`✅ getSyncData() Response - ${syncType}`);
          console.log('Status Code:', response?.StatusCode);
          console.log('Response Result Keys:', response?.Result ? Object.keys(response.Result) : 'No Result');
          
          const result = response?.Result || response?.result;
          if (result) {
            // Log summary of data received (check both camelCase and PascalCase field names)
            if (result.deviceDetailData) console.log('  - deviceDetailData: ✓');
            if (result.deviceEntityData) console.log(`  - deviceEntityData: ${Array.isArray(result.deviceEntityData) ? result.deviceEntityData.length : 'N/A'} items`);
            if (result.forms) console.log(`  - forms: ${Array.isArray(result.forms) ? result.forms.length : 'N/A'} items`);
            if (result.formBuilderDetails) console.log(`  - formBuilderDetails: ${Array.isArray(result.formBuilderDetails) ? result.formBuilderDetails.length : 'N/A'} items`);
            if (result.documents) console.log(`  - documents: ${Array.isArray(result.documents) ? result.documents.length : 'N/A'} items`);
            if (result.locations) console.log(`  - locations: ${Array.isArray(result.locations) ? result.locations.length : 'N/A'} items`);
            if (result.deviceAvailableFormDetailList) console.log(`  - deviceAvailableFormDetailList: ${Array.isArray(result.deviceAvailableFormDetailList) ? result.deviceAvailableFormDetailList.length : 'N/A'} items`);
            if (result.deviceAvailableDocumentDetailList) console.log(`  - deviceAvailableDocumentDetailList: ${Array.isArray(result.deviceAvailableDocumentDetailList) ? result.deviceAvailableDocumentDetailList.length : 'N/A'} items`);
            if (result.deviceLocationDetailList) console.log(`  - deviceLocationDetailList: ${Array.isArray(result.deviceLocationDetailList) ? result.deviceLocationDetailList.length : 'N/A'} items`);
            if (result.deviceFormDetailsList) console.log(`  - deviceFormDetailsList: ${Array.isArray(result.deviceFormDetailsList) ? result.deviceFormDetailsList.length : 'N/A'} items`);
            if (result.deviceFormBuilderDetailsList) console.log(`  - deviceFormBuilderDetailsList: ${Array.isArray(result.deviceFormBuilderDetailsList) ? result.deviceFormBuilderDetailsList.length : 'N/A'} items`);
            if (result.serverTime) console.log(`  - serverTime: ${result.serverTime}`);
            if (result.acknowledgeChangeIds) console.log(`  - acknowledgeChangeIds: ${Array.isArray(result.acknowledgeChangeIds) ? result.acknowledgeChangeIds.length : 'N/A'} items`);
          }
          console.log('========================================');
        }),
        catchError((error) => {
          console.error('========================================');
          console.error(`❌ getSyncData() Error - ${syncType}`);
          console.error('Error:', error);
          if (error?.error) {
            console.error('Error Response:', JSON.stringify(error.error, null, 2));
          }
          console.error('========================================');
          throw error;
        })
      );
    }

    // Legacy GET endpoint for initial sync or old incremental sync
    let queryParams = '';
    if (isInitialSync) {
      const params: string[] = [];
      if (request.companyId) params.push(`companyId=${request.companyId}`);
      if (request.includeLocations !== undefined) params.push(`includeLocations=${request.includeLocations}`);
      if (request.includeForms !== undefined) params.push(`includeForms=${request.includeForms}`);
      if (request.includeDocuments !== undefined) params.push(`includeDocuments=${request.includeDocuments}`);
      if (params.length > 0) {
        queryParams = '?' + params.join('&');
      }
    } else if (request.changeIds && request.changeIds.length > 0) {
      // For incremental sync, add changeIds as query parameter
      queryParams = '?changeIds=' + request.changeIds.join(',');
    }

    const url = `${this.sharedDataService.siteBaseUrl}/DeviceSync/offline/${deviceId}${queryParams}`;
    console.log(`📡 [API] Calling GET: ${url}`);

    return this.httpClient.get(
      url,
      {
        headers: this.getHeaders(),
      }
    ).pipe(
      tap((response: any) => {
        console.log('========================================');
        console.log(`✅ getSyncData() Response - ${syncType}`);
        console.log('Status Code:', response?.StatusCode);
        console.log('Response Result Keys:', response?.Result ? Object.keys(response.Result) : 'No Result');
        
        const result = response?.Result || response?.result;
        if (result) {
          // Log summary of data received
          if (result.deviceDetailData) console.log('  - deviceDetailData: ✓');
          if (result.deviceEntityData) console.log(`  - deviceEntityData: ${Array.isArray(result.deviceEntityData) ? result.deviceEntityData.length : 'N/A'} items`);
          if (result.deviceAvailableFormDetailList) console.log(`  - deviceAvailableFormDetailList: ${Array.isArray(result.deviceAvailableFormDetailList) ? result.deviceAvailableFormDetailList.length : 'N/A'} items`);
          if (result.deviceAvailableDocumentDetailList) console.log(`  - deviceAvailableDocumentDetailList: ${Array.isArray(result.deviceAvailableDocumentDetailList) ? result.deviceAvailableDocumentDetailList.length : 'N/A'} items`);
          if (result.deviceLocationDetailList) console.log(`  - deviceLocationDetailList: ${Array.isArray(result.deviceLocationDetailList) ? result.deviceLocationDetailList.length : 'N/A'} items`);
        }
        console.log('========================================');
      }),
      catchError((error) => {
        console.error('========================================');
        console.error(`❌ getSyncData() Error - ${syncType}`);
        console.error('Error:', error);
        if (error?.error) {
          console.error('Error Response:', JSON.stringify(error.error, null, 2));
        }
        console.error('========================================');
        throw error;
      })
    );
  }

  acknowledgeSync(deviceId: string, lastSyncTimestamp: string) {
    return this.httpClient.post(
      `${this.sharedDataService.siteBaseUrl}/DeviceSync/AcknowledgeSync`,
      {
        deviceId,
        lastSyncTimestamp,
      },
      {
        headers: this.getHeaders(),
      }
    );
  }

  private getHeaders(): Record<string, string> {
    const accessID = this.sharedDataService.deviceUID;
    const token = localStorage.getItem(
      EnumService.LocalStorageKeys.API_TOKEN
    );
    const headers: Record<string, string> = {};
    if (accessID) {
      headers['accessID'] = accessID;
    }
    if (token) {
      headers['token'] = token;
    }
    return headers;
  }
}
